import { create } from 'zustand';
import { playMarketIntelligenceAlarm } from '../Utils/alarmService';

export const INITIAL_MARKET_INTELLIGENCE_STATE = {
  connectionStatus: 'idle',
  isConnected: false,
  isAuthenticated: false,

  authError: null,
  error: null,

  lastMessageAt: null,
  lastHeartbeatAt: null,
  lastConnectedAt: null,
  lastDisconnectedAt: null,

  rawSnapshot: null,

  snapshotType: null,
  sentAt: null,
  tradingDate: null,
  latestUpdatedAt: null,

  marketSession: null,
  orchestrator: null,
  modules: {},

  visibleFrontendPosts: [],
  recentFrontendPosts: [],
  historicalTradingDate: null,
  historicalFrontendPosts: [],

  programGroups: {},

  updateSource: null,
  updateReason: null,

  hasHydratedPosts: false,
};

export function getPostTimestamp(post) {
  return (
    post?.created_at ||
    post?.t ||
    post?.createdAt ||
    post?.inserted_at ||
    post?.insertedAt ||
    post?.cache_updated_at ||
    post?.cacheUpdatedAt ||
    post?.updated_at ||
    post?.updatedAt ||
    ''
  );
}

export function getMarketIntelligencePostId(post) {
  if (!post) return null;

  return (
    post.id ||
    `${post.source_module || post.sourceModule || post.producer_name || post.producerName || 'market'}-${
      post.created_at || post.createdAt || post.inserted_at || post.insertedAt || ''
    }`
  );
}

function getPostScope(post) {
  return post?.post_scope || post?.postScope || null;
}

function getProducerType(post) {
  return post?.producer_type || post?.producerType || null;
}

function getProducerName(post) {
  return post?.producer_name || post?.producerName || null;
}

function getSourceModule(post) {
  return post?.source_module || post?.sourceModule || null;
}

function mergePosts(existingPosts = [], incomingPosts = []) {
  const postMap = new Map();

  existingPosts.forEach((post) => {
    const id = getMarketIntelligencePostId(post);
    if (id) postMap.set(id, post);
  });

  incomingPosts.forEach((post) => {
    const id = getMarketIntelligencePostId(post);
    if (!id) return;
    postMap.set(id, {
      ...(postMap.get(id) || {}),
      ...post,
    });
  });

  return Array.from(postMap.values()).sort((a, b) => {
    const aTime = new Date(getPostTimestamp(a)).getTime();
    const bTime = new Date(getPostTimestamp(b)).getTime();

    if (Number.isNaN(aTime) && Number.isNaN(bTime)) return 0;
    if (Number.isNaN(aTime)) return 1;
    if (Number.isNaN(bTime)) return -1;

    return aTime - bTime;
  });
}

function buildExistingPostIdSet(posts = []) {
  const ids = new Set();

  posts.forEach((post) => {
    const id = getMarketIntelligencePostId(post);
    if (id) ids.add(id);
  });

  return ids;
}

function isMarketIntelligenceModulePost(post) {
  return (
    getPostScope(post) === 'module' &&
    getProducerType(post) === 'module_formatter' &&
    Boolean(getSourceModule(post))
  );
}

function isMarketIntelligenceOrchestratorPost(post) {
  return (
    getProducerType(post) === 'orchestrator' ||
    getProducerName(post) === 'market_intelligence_organizer' ||
    getPostScope(post) === 'market'
  );
}

function shouldTriggerMarketIntelligenceAlarmForPost(post) {
  return (
    isMarketIntelligenceModulePost(post) &&
    !isMarketIntelligenceOrchestratorPost(post)
  );
}

function isInitialOrReplaySnapshot(updateSource) {
  return (
    updateSource === 'initial_snapshot' ||
    updateSource === 'snapshot_response'
  );
}

export const useMarketIntelligenceStore = create((set, get) => ({
  ...INITIAL_MARKET_INTELLIGENCE_STATE,

  setConnectionStatus: (status) => {
    set({
      connectionStatus: status,
      isConnected: status === 'connected' || status === 'authenticated',
      lastConnectedAt:
        status === 'connected' || status === 'authenticated'
          ? new Date().toISOString()
          : get().lastConnectedAt,
      lastDisconnectedAt:
        status === 'disconnected' || status === 'closed'
          ? new Date().toISOString()
          : get().lastDisconnectedAt,
    });
  },

  setAuthSuccess: () => {
    set({
      connectionStatus: 'authenticated',
      isConnected: true,
      isAuthenticated: true,
      authError: null,
      error: null,
      lastMessageAt: new Date().toISOString(),
    });
  },

  setAuthError: (error) => {
    set({
      connectionStatus: 'auth_error',
      isConnected: false,
      isAuthenticated: false,
      authError: error || 'Authentication failed',
      error: error || 'Authentication failed',
      lastMessageAt: new Date().toISOString(),
    });
  },

  setError: (error) => {
    set({
      error,
      lastMessageAt: new Date().toISOString(),
    });
  },

  setHeartbeat: () => {
    set({
      lastHeartbeatAt: new Date().toISOString(),
      lastMessageAt: new Date().toISOString(),
      error: null,
    });
  },

  setSnapshot: (snapshot, meta = {}) => {
    const previousState = get();
    const safeSnapshot = snapshot || {};

    const incomingVisiblePosts =
      safeSnapshot.visible_frontend_posts ||
      safeSnapshot.visibleFrontendPosts ||
      [];

    const incomingRecentPosts =
      safeSnapshot.recent_frontend_posts ||
      safeSnapshot.recentFrontendPosts ||
      [];

    const incomingPostsForAlert =
      incomingRecentPosts.length > 0
        ? incomingRecentPosts
        : incomingVisiblePosts;

    const existingPostIds = buildExistingPostIdSet([
      ...(previousState.visibleFrontendPosts || []),
      ...(previousState.recentFrontendPosts || []),
    ]);

    const resolvedUpdateSource =
      safeSnapshot.update_source ||
      safeSnapshot.updateSource ||
      meta.updateSource ||
      null;

    const resolvedUpdateReason =
      safeSnapshot.update_reason ||
      safeSnapshot.updateReason ||
      meta.updateReason ||
      null;

    const shouldCheckMarketIntelligenceAlarms =
      previousState.hasHydratedPosts &&
      !isInitialOrReplaySnapshot(resolvedUpdateSource);

    const newMarketIntelligenceAlertPosts = shouldCheckMarketIntelligenceAlarms
      ? incomingPostsForAlert.filter((post) => {
          const id = getMarketIntelligencePostId(post);
          if (!id || existingPostIds.has(id)) return false;
          return shouldTriggerMarketIntelligenceAlarmForPost(post);
        })
      : [];

    set({
      rawSnapshot: safeSnapshot,

      snapshotType: safeSnapshot.snapshot_type || safeSnapshot.snapshotType || null,
      sentAt: safeSnapshot.sent_at || safeSnapshot.sentAt || null,
      tradingDate: safeSnapshot.trading_date || safeSnapshot.tradingDate || null,
      latestUpdatedAt:
        safeSnapshot.latest_updated_at || safeSnapshot.latestUpdatedAt || null,

      marketSession:
        safeSnapshot.market_session || safeSnapshot.marketSession || null,

      orchestrator: safeSnapshot.orchestrator || null,
      modules: safeSnapshot.modules || {},

      visibleFrontendPosts: mergePosts(
        previousState.visibleFrontendPosts,
        incomingVisiblePosts
      ),
      recentFrontendPosts: mergePosts(
        previousState.recentFrontendPosts,
        incomingRecentPosts.length > 0 ? incomingRecentPosts : incomingVisiblePosts
      ),

      programGroups:
        safeSnapshot.program_groups || safeSnapshot.programGroups || {},

      updateSource: resolvedUpdateSource,
      updateReason: resolvedUpdateReason,

      hasHydratedPosts: true,

      lastMessageAt: new Date().toISOString(),
      error: null,
    });

    if (newMarketIntelligenceAlertPosts.length > 0) {
      playMarketIntelligenceAlarm();
    }
  },

  clearPosts: () => {
    set({
      visibleFrontendPosts: [],
      recentFrontendPosts: [],
      historicalTradingDate: null,
      historicalFrontendPosts: [],
      hasHydratedPosts: false,
    });
  },

  setHistoricalPosts: (tradingDate, posts = []) => {
    set({
      historicalTradingDate: tradingDate || null,
      historicalFrontendPosts: mergePosts([], posts),
    });
  },

  requestReset: () => {
    set({ ...INITIAL_MARKET_INTELLIGENCE_STATE });
  },

  getModuleState: (moduleName) => {
    return get().modules?.[moduleName] || null;
  },

  getPostsByModule: (moduleName) => {
    return (get().recentFrontendPosts || []).filter(
      (post) =>
        post?.source_module === moduleName ||
        post?.sourceModule === moduleName
    );
  },

  getVisiblePostsByModule: (moduleName) => {
    return (get().visibleFrontendPosts || []).filter(
      (post) =>
        post?.source_module === moduleName ||
        post?.sourceModule === moduleName
    );
  },

  getLatestVisiblePostByModule: (moduleName) => {
    const posts = get().getVisiblePostsByModule(moduleName);
    return posts[posts.length - 1] || null;
  },
}));

export default useMarketIntelligenceStore;
